<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import { filesKey } from "@/logic/provide";
import {
  actorsPathName,
  complaintsPathName,
  dentistsPathName,
  differentPathName,
  filesPathName,
  healthCentersPathName,
  infoPathName,
  signalPathName,
  templatesPatientPathName,
  templatesPraticienPathName,
  templatesAdministratifPathName,
} from "@/router";
import { useLoginStore } from "@/stores/login";
import {
  isCaseTypeDemande,
  isCaseTypeDifferent,
  isCaseTypePlainte,
  isCaseTypeSignalement,
} from "@/types";
import { computed, inject } from "vue";
import { RouterLink, useRoute } from "vue-router";
const userStore = useLoginStore();
const path = useRoute();
const pathName = computed(() => path.name);
const allFiles = inject(filesKey);

const stats = computed(() => {
  var current = {
    dossiers: 0,
    plaintes: 0,
    differends: 0,
    signalements: 0,
    demandes: 0,
  };

  if (allFiles) {
    if (!allFiles.value.list) return current;

    allFiles.value.list.forEach((file) => {
      if (
        !file.member_id ||
        (file.member_id !== userStore.currentUser?.id &&
          file.member_id !== userStore.currentUser?.legacyID)
      )
        return;

      current.dossiers++;
      if (isCaseTypePlainte(file.case_type)) current.plaintes++;
      else if (isCaseTypeDemande(file.case_type)) current.demandes++;
      else if (isCaseTypeDifferent(file.case_type)) current.differends++;
      else if (isCaseTypeSignalement(file.case_type)) current.signalements++;
    });
  }

  return current;
});
</script>

<template>
  <div class="menu-container">
    <template v-if="userStore.currentUser">
      <div class="name">
        <h3>{{ userStore.currentUser.name }}</h3>
      </div>

      <div class="links">
        <div class="sub">Gestion des dossiers</div>
        <div>
          <RouterLink
            to="/dossier"
            :class="{ highlight: pathName === filesPathName }"
            >Mes dossiers
          </RouterLink>
          <span> : {{ stats.dossiers }}</span>
        </div>
        <div class="shifted">
          <div>
            <RouterLink
              to="/dossier/p"
              :class="{ highlight: pathName === complaintsPathName }"
              >Plaintes
            </RouterLink>
            <span v-if="stats.plaintes"> : {{ stats.plaintes }}</span>
          </div>
          <div>
            <RouterLink
              to="/dossier/d"
              :class="{ highlight: pathName === differentPathName }"
              >Différends
            </RouterLink>
            <span v-if="stats.differends"> : {{ stats.differends }}</span>
          </div>
          <div>
            <RouterLink
              to="/dossier/s"
              :class="{ highlight: pathName === signalPathName }"
              >Signalements
            </RouterLink>
            <span v-if="stats.signalements"> : {{ stats.signalements }}</span>
          </div>
          <div>
            <RouterLink
              to="/dossier/i"
              :class="{ highlight: pathName === infoPathName }"
              >Demandes
            </RouterLink>
            <span v-if="stats.demandes"> : {{ stats.demandes }}</span>
          </div>
        </div>

        <!-- <RouterLink to="/maincourante">Main courrante</RouterLink> -->

        <div class="spacer"></div>

        <div class="sub">Bases de données</div>
        <RouterLink
          to="/plaignant"
          :class="{ highlight: pathName === actorsPathName }"
          >Fichier des Requérants</RouterLink
        >
        <RouterLink
          to="/dentiste"
          :class="{ highlight: pathName === dentistsPathName }"
          >Fichier des Praticiens</RouterLink
        >
        <RouterLink
          to="/centre"
          :class="{ highlight: pathName === healthCentersPathName }"
          >Fichier des Centres</RouterLink
        >

        <div class="spacer"></div>

        <div class="sub">Gestion des templates</div>

        <RouterLink
          to="/templates/patient"
          :class="{ highlight: pathName === templatesPatientPathName }"
          >Patients</RouterLink
        >
        <RouterLink
          to="/templates/praticien"
          :class="{ highlight: pathName === templatesPraticienPathName }"
          >Praticiens & Centres</RouterLink
        >
        <RouterLink
          to="/templates/administratif"
          :class="{ highlight: pathName === templatesAdministratifPathName }"
          >Administratif</RouterLink
        >
      </div>
    </template>
    <template v-else>
      <div class="name"></div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.menu-container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 100%;
}

.name {
  padding: 2em 0;
}

.links {
  display: flex;
  flex-direction: column;
  row-gap: 2px;

  a {
    padding-left: 0.5em;
  }

  .highlight {
    font-weight: bolder;
  }

  .shifted {
    display: flex;
    flex-direction: column;
    padding-left: 1em;
  }
}

.sub {
  padding-bottom: 0.25em;
  font-weight: 500;
}

.spacer {
  padding-bottom: 2em;
}
</style>
